import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useMediaQuery } from "@mui/material";
import { useAtomValue } from "jotai";
import { backgroundColorState, lighterBackgroundColorState, dividerState, ortexColorState, positiveState, negativeState, neutralState, textColorState, greyState } from "@/lib/store";
import Module from "@/components/module/Module";
import Tabs, { TabItem } from "@/components/tabs/Tabs";
import { Stock } from "@/models/stock";
import { EarningsFinancials, EventListRow, MixedEventStat, MixedEventStatistics, QuarterlyStatistics, QuarterlyStatRow } from "@/models/event-page";
import { getCompanyEarningsFinancials, getCompanyEventsList, getCompanyMixedEventStatistics, getCompanyQuarterlyStatistics, getEventStatsAISummary } from "@/controllers/eventPage";
import { EventStatsAISummary } from "@/models/event-page";
import { buildReactionHistory, eventStatus, indexFinancialsByEvent, isEarningsRow, matchFinancialsForEvent, sameEventType } from "./helpers";
import EventsRail from "./EventsRail";
import EventHero from "./EventHero";
import StatsTable from "./StatsTable";
import HitRates from "./HitRates";
import AISummaryCard from "./AISummaryCard";
import PriceEventsChart from "./PriceEventsChart";
import ReactionHistoryChart from "./ReactionHistoryChart";
import TranscriptHub from "./TranscriptHub";
import NoTranscriptFill from "./NoTranscriptFill";
import Lockable from "@/components/lockable/Lockable";
import styles from "./EventPageRevamp.module.scss";
import { alpha, brighten } from "@/lib/ortex-charts/color";
import { eventKey, eventSelectionKey, findLinkedEvent, stockEventPath } from "@/lib/event-link";

type View = "statistics" | "events";

// Top-level view toggle, expressed for the shared <Tabs> component
// (value === View). Mirrors StockEvents.tsx's `pageTabs`. Mobile uses the same
// two tabs and the same module set as desktop — only the layout stacks.
const VIEW_TABS: TabItem[] = [
    { title: "Events Statistics", value: "statistics" },
    { title: "Events", value: "events" },
];

/**
 * ±window (ms) within which a mixed-stats row counts as the SAME occurrence as the
 * selected event. CIQ emits a quarter's release / announcement / call within days
 * of each other; the previous occurrence is a full quarter away.
 */
const MIXED_STAT_MATCH_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // ±7 days

interface EventPageRevampProps {
    stock: Stock;
    /** Event key from a `/events/<eventKey>` link: opens the Events view on that event. */
    linkedEventKey?: string | null;
}

/**
 * Points the address bar at `path` without a navigation or a new history entry.
 * Next's own history state is kept (with its `as` updated), so back/forward still
 * restore this page. Query and hash survive.
 */
const replaceAddressPath = (path: string) => {
    if (typeof window === "undefined" || window.location.pathname === path) return;
    const as = path + window.location.search + window.location.hash;
    const state = window.history.state;
    window.history.replaceState(state?.__N ? { ...state, as } : state, "", as);
};

/**
 * Revamped stock event page body (issue #1633). Renders the events rail plus the
 * two top-level views (Events Statistics / Events), as a desktop split layout or
 * mobile tabbed layout. Only the page BODY — the app header / menu / sidebar are
 * untouched. All colours come from the Jotai theme atoms, injected as CSS variables
 * on the root so SCSS stays theme-agnostic.
 */
const EventPageRevamp: FC<EventPageRevampProps> = ({ stock, linkedEventKey = null }) => {
    const accent = useAtomValue(ortexColorState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const neutral = useAtomValue(neutralState);
    const textColor = useAtomValue(textColorState);
    const grey = useAtomValue(greyState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const divider = useAtomValue(dividerState);

    const isMobile = useMediaQuery("(max-width: 1279px)");

    const [events, setEvents] = useState<EventListRow[]>([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [financials, setFinancials] = useState<EarningsFinancials[]>([]);
    const [quarterly, setQuarterly] = useState<QuarterlyStatistics | null>(null);
    const [quarterlyLoading, setQuarterlyLoading] = useState(true);
    const [mixed, setMixed] = useState<MixedEventStatistics>({ events: [] });
    const [statsSummary, setStatsSummary] = useState<EventStatsAISummary | null>(null);
    const [statsSummaryLoading, setStatsSummaryLoading] = useState(true);

    // Selection is by event key, not `id`: the list mixes event models whose ids collide.
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [view, setView] = useState<View>("statistics");
    // The rail scrolls to a linked event once, on load.
    const [scrollToKey, setScrollToKey] = useState<string | null>(null);
    // The URL is only rewritten once a link has been resolved, so it is not stripped first.
    const [linkResolved, setLinkResolved] = useState(false);
    const linkedKeyRef = useRef(linkedEventKey);
    linkedKeyRef.current = linkedEventKey;

    const companyId = stock.company_id;

    // Inject theme colours as CSS variables consumed by the SCSS module.
    const rootStyle = useMemo(() => {
        const dim = brighten(textColor, 0.35);
        const border = divider;
        return {
            ["--ev-accent"]: accent,
            // Faint + slightly-stronger accent washes for earnings-row emphasis.
            // Derived from the accent via Color.setOpacity so the tint follows the
            // theme accent (which is an rgb() string, not hex — so no hex-alpha).
            ["--ev-accent-tint"]: alpha(accent, 0.07),
            ["--ev-accent-tint-strong"]: alpha(accent, 0.14),
            ["--ev-up"]: positive,
            ["--ev-down"]: negative,
            ["--ev-text"]: textColor,
            ["--ev-text-dim"]: dim,
            ["--ev-bg"]: backgroundColor,
            ["--ev-bg-lighter"]: lighterBackgroundColor,
            ["--ev-border"]: border,
            ["--ev-amber"]: neutral,
        } as CSSProperties;
    }, [accent, positive, negative, textColor, backgroundColor, lighterBackgroundColor, divider, neutral, grey]);

    // ---- data fetch ----
    useEffect(() => {
        // Every call below is company-scoped. `stock.company_id` is the populated
        // snake_case field on the Stock model (same one the legacy StockEvents view
        // uses); if it ever resolves falsy, firing `/company/undefined/...` requests
        // is pointless — bail and surface it once for debugging instead.
        if (!companyId) {
            console.warn("[EventPageRevamp] missing company id on stock — skipping company-scoped event calls", { stock_id: stock.stock_id, ticker: stock.ticker });
            setEventsLoading(false);
            setLinkResolved(true);
            setQuarterlyLoading(false);
            setStatsSummaryLoading(false);
            return;
        }

        const controller = new AbortController();

        setEventsLoading(true);
        // Next keeps this component mounted across tickers: hold the address until the
        // new list lands, or it would pair the new ticker with the old event key.
        setLinkResolved(false);
        getCompanyEventsList(companyId, controller.signal)
            .then((rows) => {
                setEvents(rows);
                // A link to one event opens the Events view on it. A key that is no
                // longer in the list (or never was) falls back to the normal page.
                const linked = linkedKeyRef.current ? findLinkedEvent(rows, linkedKeyRef.current) : undefined;
                if (linked) {
                    const key = eventSelectionKey(linked);
                    setSelectedKey(key);
                    setScrollToKey(key);
                    setView("events");
                } else if (rows.length > 0) {
                    // Land on the most recent PAST earnings event with a transcript so the
                    // detail view is populated on load (not the upcoming event, which has no
                    // financials/transcript). Fall back to the most recent past earnings,
                    // then the newest event overall.
                    const now = Date.now();
                    const byRecent = (a: EventListRow, b: EventListRow) => (b.date ?? 0) - (a.date ?? 0);
                    const pastEarnings = rows.filter((r) => isEarningsRow(r) && r.date != null && r.date <= now);
                    const withTx = pastEarnings.filter((r) => r.hasTranscripts).sort(byRecent);
                    const defaultRow = withTx[0] ?? [...pastEarnings].sort(byRecent)[0] ?? [...rows].sort(byRecent)[0];
                    setSelectedKey(eventSelectionKey(defaultRow));
                }
                setLinkResolved(true);
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setEvents([]);
                setLinkResolved(true);
            })
            .finally(() => setEventsLoading(false));

        getCompanyEarningsFinancials(companyId, controller.signal)
            .then(setFinancials)
            .catch(() => setFinancials([]));

        setQuarterlyLoading(true);
        setStatsSummaryLoading(true);
        getCompanyQuarterlyStatistics(companyId, controller.signal)
            .then((stats) => {
                setQuarterly(stats);
                // The quarterly response carries the FREE-user lock signal (`obfuscated`).
                // Only free users are fully locked, so skip the stats-AI fetch just for
                // them. For BASIC users we DO fire it — the backend returns
                // `{ obfuscated: true }` cheaply (no generation), and that response is how
                // we detect the BASIC tier to gate the AI modules (lockedAI). Chained off
                // the quarterly result so the free-lock state is known before firing.
                if (stats?.obfuscated === true) {
                    setStatsSummary(null);
                    setStatsSummaryLoading(false);
                    return;
                }
                getEventStatsAISummary(companyId, controller.signal)
                    .then(setStatsSummary)
                    .catch(() => setStatsSummary(null))
                    .finally(() => setStatsSummaryLoading(false));
            })
            .catch(() => {
                setQuarterly(null);
                setStatsSummary(null);
                setStatsSummaryLoading(false);
            })
            .finally(() => setQuarterlyLoading(false));

        getCompanyMixedEventStatistics(companyId, controller.signal)
            .then(setMixed)
            .catch(() => setMixed({ events: [] }));

        return () => controller.abort();
    }, [companyId]);

    const financialsByEvent = useMemo(() => indexFinancialsByEvent(financials), [financials]);
    const mixedEvents = useMemo(() => mixed?.events ?? [], [mixed]);

    const selectedEvent = useMemo(() => events.find((e) => eventSelectionKey(e) === selectedKey) ?? events[0], [events, selectedKey]);
    const selectedLinkKey = selectedEvent ? eventKey(selectedEvent) : null;

    // Keep the address bar a link to what is on screen: the selected event in the
    // Events view, the plain events page in the Statistics view.
    useEffect(() => {
        if (!linkResolved) return;
        replaceAddressPath(stockEventPath(stock, view === "events" ? selectedLinkKey : null));
    }, [linkResolved, view, selectedLinkKey, stock.exchangesymbol, stock.ticker]);
    // Match financials by nearest reporting period, not just the exact event_id:
    // the selected event is often the earnings CALL while financials are keyed by
    // the announcement event of the same quarter (see matchFinancialsForEvent).
    const selectedFinancials = useMemo(() => matchFinancialsForEvent(financials, selectedEvent) ?? undefined, [financials, selectedEvent]);

    // Mixed-event stat row matching the selected event (by date proximity / type).
    // Reported events only: the stat rows are REALIZED reactions, so an upcoming
    // event must not borrow the previous occurrence's next-day move. The window is
    // tight because same-quarter siblings (release / announcement / call) land within
    // days of each other, while the previous occurrence is a quarter away.
    const selectedMixedStat: MixedEventStat | undefined = useMemo(() => {
        if (!selectedEvent || eventStatus(selectedEvent) !== "reported") return undefined;
        const sameType = mixedEvents.filter((m) => sameEventType(m?.name, selectedEvent.type));
        if (sameType.length === 0) return undefined;
        // closest by date
        const closest = sameType.reduce((best, cur) => (Math.abs((cur.date ?? 0) - selectedEvent.date) < Math.abs((best.date ?? 0) - selectedEvent.date) ? cur : best), sameType[0]);
        return Math.abs((closest.date ?? 0) - selectedEvent.date) <= MIXED_STAT_MATCH_WINDOW_MS ? closest : undefined;
    }, [selectedEvent, mixedEvents]);

    const typeStats = useMemo(() => (selectedEvent ? mixedEvents.filter((m) => sameEventType(m?.name, selectedEvent.type)) : []), [selectedEvent, mixedEvents]);
    const reactionHistory = useMemo(() => (selectedEvent ? buildReactionHistory(mixedEvents, selectedEvent.type) : []), [selectedEvent, mixedEvents]);

    // Aggregates are nested inside the "Earnings Total" row (not a top-level key).
    const earningsTotal = quarterly?.["Earnings Total"] as QuarterlyStatRow | undefined;
    const aggregates = earningsTotal?.aggregates ?? null;
    // Directional next-day average (signed), to match the stats table's "Next Trading Day".
    const avgNextDay = earningsTotal?.["1DayAfterChangePct"]?.avg ?? null;

    // Two lock levels, matching the backend's two-tier gating:
    //  - lockedStats (FREE): the Basic+ endpoints (quarterly stats, mixed/reaction,
    //    transcript TEXT) obfuscate only for free users and flag the response. Gates
    //    the non-AI advanced modules — stats table, reaction reliability, reaction
    //    history chart, no-transcript fill.
    //  - lockedAI (FREE or BASIC): the Advanced+ AI endpoints (stats AI summary, call
    //    AI summary, Q&A) return `{ obfuscated: true }` for below-Advanced viewers. We
    //    detect the BASIC tier via the stats-summary response (Basic gets stats text
    //    but not the AI summary). Gates the AI modules.
    // The events rail and price chart stay free for everyone.
    const lockedStats = quarterly?.obfuscated === true;
    const lockedAI = lockedStats || statsSummary?.obfuscated === true;

    const onSelect = (key: string) => {
        setSelectedKey(key);
        setView("events");
    };

    // ---- shared section renderers ----
    const railNode = <EventsRail events={events} financialsByEvent={financialsByEvent} selectedKey={selectedEvent ? eventSelectionKey(selectedEvent) : null} onSelect={onSelect} scrollToKey={scrollToKey} loading={eventsLoading} />;

    const statisticsViewMain = (
        <div className={`${styles.viewMain} ${styles.viewMainStats}`}>
            <div className={styles.statsGrid}>
                <div className={styles.statsColMain}>
                    {/* FREE: price & events chart stays visible for everyone. */}
                    <Module title={`${stock.exchangesymbol}:${stock.ticker} Price & Events`} feedback>
                        <PriceEventsChart stock={stock} mixedStats={mixedEvents} />
                    </Module>
                    <Module title="Earnings Event Statistics">
                        <Lockable locked={lockedStats} minHeight={240}>
                            {quarterlyLoading ? <div className={styles.loading}>Loading statistics…</div> : <StatsTable stats={quarterly} />}
                        </Lockable>
                    </Module>
                </div>
                <div className={styles.statsColSide}>
                    {lockedAI ? (
                        <Lockable locked minHeight={200}>
                            <AISummaryCard unavailable />
                        </Lockable>
                    ) : statsSummaryLoading ? (
                        <Module title="AI Summary">
                            <div className={styles.loading}>Generating summary…</div>
                        </Module>
                    ) : (
                        <AISummaryCard headline={statsSummary?.headline} confidence={statsSummary?.confidence} bullets={statsSummary?.bullets} takeaway={statsSummary?.takeaway} unavailable={!statsSummary} />
                    )}
                    {(aggregates || lockedStats) && (
                        <Module title="Reaction reliability">
                            <Lockable locked={lockedStats} minHeight={160}>
                                {aggregates ? <HitRates aggregates={aggregates} avgNextDay={avgNextDay} /> : <div className={styles.loading}>&nbsp;</div>}
                            </Lockable>
                        </Module>
                    )}
                </div>
            </div>
        </div>
    );

    const eventsViewMain = selectedEvent ? (
        <section className={`${styles.viewMain} ${styles.viewMainEvents}`} aria-label="Event detail">
            {/* Desktop top row: hero on the left, reaction-history chart on the right
                (each ~half width) to cut vertical height. When there is no reaction
                history, the single-child modifier lets the hero span the full width. */}
            <div className={`${styles.eventTopRow} ${reactionHistory.length === 0 ? styles.eventTopRowSingle : ""}`}>
                <EventHero event={selectedEvent} financials={selectedFinancials} mixedStat={selectedMixedStat} shareable={selectedLinkKey != null} />
                {reactionHistory.length > 0 && (
                    <Module title={`${selectedEvent.type || "Event"} events: price reaction history`}>
                        <Lockable locked={lockedStats} minHeight={240}>
                            <ReactionHistoryChart occurrences={reactionHistory} />
                        </Lockable>
                    </Module>
                )}
            </div>
            {selectedEvent.hasTranscripts ? (
                // Transcript TEXT is Basic+, so only FREE users (lockedStats) get the
                // whole hub blurred. Basic+ see the hub with just the AI parts gated
                // internally on lockedAI — no outer Lockable. The hub carries its own
                // inner cards (AI summary), so it is NOT wrapped in an outer Module.
                lockedStats ? (
                    <Lockable locked minHeight={320}>
                        <TranscriptHub event={selectedEvent} lockedAI />
                    </Lockable>
                ) : (
                    <TranscriptHub event={selectedEvent} lockedAI={lockedAI} />
                )
            ) : (
                <Module title={null} noHeader>
                    <Lockable locked={lockedStats} minHeight={200}>
                        <NoTranscriptFill event={selectedEvent} typeStats={typeStats} />
                    </Lockable>
                </Module>
            )}
        </section>
    ) : (
        <div className={styles.viewMain}>
            <div className={styles.empty}>Select an event to see its detail.</div>
        </div>
    );

    // ---- mobile layout ----
    // Same two tabs and the same module set as desktop (the Events view already
    // includes the transcript hub). Only the layout differs: the desktop grid
    // collapses to a single stacked column below $tablet-large-width — exactly the
    // `isMobile` breakpoint — and the sidebar rail is replaced by a compact <select>.
    if (isMobile) {
        return (
            <div className={styles.root} style={rootStyle}>
                <div className={styles.toolbar}>
                    <div className={styles.toolbarTabs}>
                        <Tabs
                            activeTab={view}
                            alignment="center"
                            handleChange={(_e, newView) => newView && setView(newView as View)}
                            module="events"
                            route="s/[exchange]/[ticker]/events"
                            setState={(v) => setView(v as View)}
                            tabs={VIEW_TABS}
                            setDefault={false}
                        />
                    </div>
                </div>

                {view === "events" && events.length > 0 && (
                    <label className={styles.mSelectRow}>
                        <span className={styles.mSelectLabel}>Selected event</span>
                        <select value={selectedEvent ? eventSelectionKey(selectedEvent) : ""} onChange={(e) => onSelect(e.target.value)}>
                            {events.map((e) => (
                                <option key={eventSelectionKey(e)} value={eventSelectionKey(e)}>
                                    {e.title}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                {view === "statistics" ? statisticsViewMain : eventsViewMain}
            </div>
        );
    }

    // ---- desktop layout ----
    return (
        <div className={styles.root} style={rootStyle}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarTabs}>
                    <Tabs
                        activeTab={view}
                        alignment="center"
                        handleChange={(_e, newView) => newView && setView(newView as View)}
                        module="events"
                        route="s/[exchange]/[ticker]/events"
                        setState={(v) => setView(v as View)}
                        tabs={VIEW_TABS}
                        setDefault={false}
                    />
                </div>
            </div>

            <div className={view === "statistics" ? styles.statsView : styles.eventsView}>
                {railNode}
                {view === "statistics" ? statisticsViewMain : eventsViewMain}
            </div>
        </div>
    );
};

export default EventPageRevamp;
